import { useState } from 'react'
import type { ManagerialDashboardSection } from '@/types/api'
import { DashboardSectionStatus } from './components/DashboardSectionStatus'
import { EMPTY_ACTIVE_CAMPAIGN_VIEWS, mapActiveCampaigns } from './lib/active-campaigns'
import { EMPTY_RECENT_ORDER_VIEWS, mapRecentOrders } from './lib/recent-orders'
import {
  EMPTY_CAMPAIGN_PERFORMANCE_VIEWS,
  mapCampaignPerformance,
} from './lib/campaign-performance'
import {
  EMPTY_CLIENT_PROFITABILITY_VIEWS,
  mapClientProfitability,
} from './lib/client-profitability'
import {
  ACTIVE_AFFILIATES_KPI_ID,
  ACTIVE_CAMPAIGNS_KPI_ID,
  ACTIVE_CLIENTS_KPI_ID,
  ACTIVE_INSTITUTIONS_KPI_ID,
  AVERAGE_TICKET_KPI_ID,
  applyLiveManagerialKpis,
  getPlaceholderManagerialKpiViews,
  NET_REVENUE_KPI_ID,
  PAID_ORDERS_KPI_ID,
  PLATFORM_PROFIT_KPI_ID,
  PLATFORM_MARGIN_KPI_ID,
  SOLD_NUMBERS_KPI_ID,
  LEADS_KPI_ID,
  CONVERSION_RATE_KPI_ID,
} from './lib/managerial-kpis'
import { buildRevenueOriginBreakdown } from './lib/revenue-origin'
import {
  ALL_CAMPAIGNS_OPTION,
  ALL_INSTITUTIONS_OPTION,
  buildExecutiveCampaignOptions,
  buildExecutiveInstitutionOptions,
} from './lib/executive-filters'
import { buildOrderStatusBreakdown, EMPTY_ORDER_STATUS_COUNTS } from './lib/order-status'
import {
  EMPTY_SALES_EVOLUTION_POINTS,
  mapSalesEvolutionPoints,
} from './lib/sales-evolution'
import { useExecutiveFilters } from './hooks/useExecutiveFilters'
import { useManagerialDashboard } from './hooks/useManagerialDashboard'
import { useSalesEvolutionChart } from './hooks/useSalesEvolutionChart'
import { ActiveCampaignsPanel } from './components/ActiveCampaignsPanel'
import { CampaignPerformancePanel } from './components/CampaignPerformancePanel'
import { ClientProfitabilityPanel } from './components/ClientProfitabilityPanel'
import { ExecutiveHeader } from './components/ExecutiveHeader'
import { KpiStrip } from './components/KpiStrip'
import { OrderStatusChart } from './components/OrderStatusChart'
import { RecentOrdersPanel } from './components/RecentOrdersPanel'
import { RevenueOriginChart } from './components/RevenueOriginChart'
import { SalesEvolutionChart } from './components/SalesEvolutionChart'
import { ShareAccessModal } from './components/ShareAccessModal'
import { useAuth } from '@/hooks/useAuth'
import { InvestorMetrics } from './components/ExecutiveFinance/InvestorMetrics'
import { MeasuredInsights } from './components/ExecutiveFinance/MeasuredInsights'
import { RevenueExpensesChart } from './components/ExecutiveFinance/RevenueExpensesChart'
import styles from './DashGerencialPage.module.css'

/**
 * Visão administrativa ou compartilhada com métricas persistidas.
 * Carregamento e falhas nunca são substituídos por números de demonstração.
 */
export function DashGerencialPage() {
  const { guard } = useAuth()
  const canShareAccess = guard === 'admin'
  const dashboardAudience = guard === 'institution' ? 'institution' : 'admin'
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const executiveFilters = useExecutiveFilters()
  const salesEvolution = useSalesEvolutionChart()
  const dashboard = useManagerialDashboard(
    executiveFilters.filters,
    salesEvolution,
    dashboardAudience
  )
  const kpis = applyLiveManagerialKpis(
    getPlaceholderManagerialKpiViews(),
    {
      [LEADS_KPI_ID]: dashboard.data?.kpis?.leads ?? null,
      [CONVERSION_RATE_KPI_ID]: dashboard.data?.kpis?.conversion_rate ?? null,
      [PLATFORM_PROFIT_KPI_ID]: dashboard.data?.kpis?.platform_profit ?? null,
      [PLATFORM_MARGIN_KPI_ID]: dashboard.data?.kpis?.platform_margin ?? null,
      [SOLD_NUMBERS_KPI_ID]: dashboard.data?.kpis?.sold_numbers ?? null,
      [ACTIVE_INSTITUTIONS_KPI_ID]: dashboard.data?.kpis?.active_institutions ?? null,
      [PAID_ORDERS_KPI_ID]: dashboard.data?.kpis?.paid_orders ?? null,
      [AVERAGE_TICKET_KPI_ID]: dashboard.data?.kpis?.average_ticket ?? null,
      [ACTIVE_CLIENTS_KPI_ID]: dashboard.data?.kpis?.active_clients ?? null,
      [ACTIVE_CAMPAIGNS_KPI_ID]: dashboard.data?.kpis?.active_campaigns ?? null,
      [ACTIVE_AFFILIATES_KPI_ID]: dashboard.data?.kpis?.active_affiliates ?? null,
      [NET_REVENUE_KPI_ID]: dashboard.data?.kpis?.revenue ?? null,
    },
    dashboard.sections.overview.isLoading
  )
  const institutionOptions = dashboard.filterOptions
    ? buildExecutiveInstitutionOptions(dashboard.filterOptions.institutions)
    : [ALL_INSTITUTIONS_OPTION]
  const campaignOptions = dashboard.filterOptions
    ? buildExecutiveCampaignOptions(dashboard.filterOptions.campaigns)
    : [ALL_CAMPAIGNS_OPTION]
  const salesEvolutionPoints = dashboard.data?.sales_evolution
    ? mapSalesEvolutionPoints(dashboard.data.sales_evolution.points)
    : EMPTY_SALES_EVOLUTION_POINTS
  const orderStatusBreakdown = buildOrderStatusBreakdown(
    dashboard.data?.order_status ?? EMPTY_ORDER_STATUS_COUNTS
  )
  const campaigns = dashboard.data?.active_campaigns
    ? mapActiveCampaigns(dashboard.data.active_campaigns)
    : EMPTY_ACTIVE_CAMPAIGN_VIEWS
  const recentOrders = dashboard.data?.recent_orders
    ? mapRecentOrders(dashboard.data.recent_orders)
    : EMPTY_RECENT_ORDER_VIEWS
  const clientProfitability = dashboard.data?.client_profitability
    ? mapClientProfitability(dashboard.data.client_profitability)
    : EMPTY_CLIENT_PROFITABILITY_VIEWS
  const campaignPerformance = dashboard.data?.campaign_performance
    ? mapCampaignPerformance(dashboard.data.campaign_performance)
    : EMPTY_CAMPAIGN_PERFORMANCE_VIEWS
  const generatedAt = dashboard.sections.overview.data
    ? Date.parse(dashboard.sections.overview.data.generated_at)
    : Number.NaN

  const blockStatus = (section: ManagerialDashboardSection) => (
    <DashboardSectionStatus
      section={section}
      state={dashboard.sections[section]}
      onRetry={() => dashboard.retry(section)}
    />
  )
  const measurementWindows = Object.fromEntries(
    (['finance', 'relationship', 'buyers', 'products', 'funnel'] as const).flatMap(
      (section) => {
        const group = dashboard.sections[section].data?.measurements
        return group
          ? group.sections.map((item) => [
              item.id,
              { from: group.date_from, to: group.date_to },
            ])
          : []
      }
    )
  )

  return (
    <div className={styles.page}>
      <ExecutiveHeader
        filters={executiveFilters.filters}
        institutionOptions={institutionOptions}
        campaignOptions={campaignOptions}
        canShareAccess={canShareAccess}
        onShareAccess={() => setIsShareModalOpen(true)}
        onPeriodChange={executiveFilters.setPeriod}
        onInstitutionChange={executiveFilters.setInstitution}
        onCampaignChange={executiveFilters.setCampaign}
        onStatusChange={executiveFilters.setStatus}
      />
      {dashboard.isLoading ? (
        <div
          className={styles.loadingNotice}
          role="status"
          aria-label="Carregamento do painel"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className={styles.loadingSpinner} aria-hidden="true" />
          <div className={styles.loadingCopy}>
            <strong>
              {dashboard.data
                ? 'Atualizando informações...'
                : 'Carregando informações...'}
            </strong>
            <span>
              Os blocos aparecem conforme ficam prontos. {dashboard.loadedCount}/
              {dashboard.sectionCount} carregados.
            </span>
          </div>
        </div>
      ) : null}
      {dashboard.error ? (
        <p className={styles.error} role="alert">
          {dashboard.error}
        </p>
      ) : null}
      {dashboard.chartNotice ? (
        <p className={styles.error} role="status">
          {dashboard.chartNotice}
        </p>
      ) : null}
      <div className={styles.refreshControls}>
        <span>
          Atualização por blocos a cada minuto enquanto esta página estiver visível.
        </span>
        <button type="button" onClick={dashboard.refresh} disabled={dashboard.isLoading}>
          {dashboard.isLoading ? 'Atualizando...' : 'Atualizar agora'}
        </button>
      </div>
      {blockStatus('overview')}
      <KpiStrip items={kpis} />
      {Number.isFinite(generatedAt) ? (
        <p className={styles.snapshotNote}>
          Indicadores atualizados em{' '}
          {new Intl.DateTimeFormat('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
            timeZone: 'America/Sao_Paulo',
          }).format(new Date(generatedAt))}{' '}
          · horário de Brasília
        </p>
      ) : null}
      {blockStatus('finance')}
      <RevenueExpensesChart
        finance={dashboard.data?.executive_finance}
        isLoading={dashboard.sections.finance.isLoading}
      />
      <div className={styles.chartsRow}>
        <div className={styles.salesEvolutionSlot}>
          {blockStatus('sales_evolution')}
          <SalesEvolutionChart
            points={salesEvolutionPoints}
            period={salesEvolution.period}
            customFrom={salesEvolution.customFrom}
            customTo={salesEvolution.customTo}
            isLoading={
              dashboard.sections.sales_evolution.isLoading &&
              !dashboard.sections.sales_evolution.data
            }
            isBusy={dashboard.sections.sales_evolution.isLoading}
            hasData={dashboard.sections.sales_evolution.data !== null}
            onPeriodChange={salesEvolution.selectPeriod}
            onCustomFromChange={salesEvolution.setCustomFrom}
            onCustomToChange={salesEvolution.setCustomTo}
          />
        </div>
        <div className={styles.orderStatusSlot}>
          <OrderStatusChart
            breakdown={orderStatusBreakdown}
            isLoading={
              dashboard.sections.overview.isLoading && !dashboard.sections.overview.data
            }
            isBusy={dashboard.sections.overview.isLoading}
            hasData={dashboard.sections.overview.data !== null}
          />
        </div>
      </div>
      <div className={styles.listsRow}>
        <div className={styles.listSlot}>
          {blockStatus('activity')}
          <ActiveCampaignsPanel
            campaigns={campaigns}
            isLoading={
              dashboard.sections.activity.isLoading && !dashboard.sections.activity.data
            }
            isBusy={dashboard.sections.activity.isLoading}
            hasData={dashboard.sections.activity.data !== null}
          />
        </div>
        <div className={styles.listSlot}>
          <RecentOrdersPanel
            orders={recentOrders}
            isLoading={
              dashboard.sections.activity.isLoading && !dashboard.sections.activity.data
            }
            isBusy={dashboard.sections.activity.isLoading}
            hasData={dashboard.sections.activity.data !== null}
          />
        </div>
      </div>
      <div className={styles.originRow}>
        <div className={styles.originSlot}>
          {blockStatus('revenue_origin')}
          <RevenueOriginChart
            breakdown={
              dashboard.data?.revenue_origin
                ? buildRevenueOriginBreakdown(dashboard.data.revenue_origin)
                : null
            }
            isLoading={dashboard.sections.revenue_origin.isLoading}
          />
        </div>
        <div className={styles.profitabilitySlot}>
          {blockStatus('client_profitability')}
          <ClientProfitabilityPanel
            rows={clientProfitability}
            isLoading={
              dashboard.sections.client_profitability.isLoading &&
              !dashboard.sections.client_profitability.data
            }
            isBusy={dashboard.sections.client_profitability.isLoading}
            hasData={dashboard.sections.client_profitability.data !== null}
          />
        </div>
      </div>
      <div className={styles.performanceRow}>
        {blockStatus('campaign_performance')}
        <CampaignPerformancePanel
          rows={campaignPerformance}
          isLoading={
            dashboard.sections.campaign_performance.isLoading &&
            !dashboard.sections.campaign_performance.data
          }
          isBusy={dashboard.sections.campaign_performance.isLoading}
          hasData={dashboard.sections.campaign_performance.data !== null}
        />
      </div>
      {['relationship', 'buyers', 'products', 'funnel'].map((section) => (
        <div key={section}>{blockStatus(section as ManagerialDashboardSection)}</div>
      ))}
      <MeasuredInsights
        data={dashboard.data?.measurements}
        isLoading={(
          ['finance', 'relationship', 'buyers', 'products', 'funnel'] as const
        ).some((section) => dashboard.sections[section].isLoading)}
        windows={measurementWindows}
      />
      <InvestorMetrics
        finance={dashboard.data?.executive_finance}
        isLoading={dashboard.sections.finance.isLoading}
      />
      {canShareAccess && isShareModalOpen ? (
        <ShareAccessModal isOpen onClose={() => setIsShareModalOpen(false)} />
      ) : null}
    </div>
  )
}
